import { createFileRoute, Link, Outlet, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Map, Users, UserSquare, Settings, Inbox, LogOut, Wrench } from "lucide-react";
import { Brand } from "@/components/brand";


export const Route = createFileRoute("/_authenticated/admin")({
  staticData: { sitemap: false },
  component: AdminLayout,
});

function AdminLayout() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    (async () => {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        navigate({ to: "/auth" });
        return;
      }
      const { data: roleRow, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userData.user.id)
        .eq("role", "admin")
        .maybeSingle();
      if (error) console.error("role check error", error);
      setIsAdmin(!!roleRow);
      setChecking(false);
    })();
  }, [navigate]);

  async function signOut() {
    await supabase.auth.signOut();
    toast.success("Signed out");
    navigate({ to: "/" });
  }

  if (checking) {
    return (
      <div className="min-h-screen grid place-items-center bg-paper text-mono-caps text-ink/40">
        Verifying credentials…
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen grid place-items-center bg-paper px-6">
        <div className="max-w-md text-center">
          <p className="text-mono-caps text-amber mb-3">403 · Not authorized</p>
          <h1 className="text-3xl mb-3">This is the backoffice</h1>
          <p className="text-ink/60 mb-6">
            El teu compte no té permisos d'administrador.
          </p>
          <button onClick={signOut} className="bg-ink text-paper px-4 py-2 hover:bg-amber transition-colors">
            Sign out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper flex">
      {/* Sidebar */}
      <aside className="w-60 border-r border-ink/8 flex flex-col p-4 sticky top-0 h-screen">
        <Brand size="sm" />
        <p className="text-mono-caps text-amber mb-8 mt-2">Backoffice</p>

        <nav className="space-y-0.5 text-sm">
          <NavItem to="/admin/creators" icon={UserSquare} label="Youtubers" />
          <NavItem to="/admin" icon={Users} label="Channels" exact />
          <NavItem to="/admin/inbox" icon={Inbox} label="Inbox" />
          <NavItem to="/admin/maintenance" icon={Wrench} label="Maintenance" />
          <NavItem to="/admin/settings" icon={Settings} label="Settings" />
        </nav>
        <div className="mt-auto space-y-1 pt-6 border-t border-ink/8">
          <Link
            to="/"
            className="flex items-center gap-2 px-3 py-2 text-sm text-ink/60 hover:bg-secondary hover:text-ink transition-colors"
          >
            <Map className="size-4" /> View site
          </Link>
          <button
            onClick={signOut}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-ink/60 hover:bg-secondary hover:text-ink transition-colors"
          >
            <LogOut className="size-4" /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <Outlet />
      </div>
    </div>
  );
}

function NavItem({
  to,
  icon: Icon,
  label,
  exact,
}: {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  exact?: boolean;
}) {
  return (
    <Link
      to={to}
      activeProps={{ className: "bg-ink text-paper" }}
      activeOptions={{ exact }}
      className="flex items-center gap-2.5 px-3 py-2 text-ink/70 hover:bg-secondary transition-colors"
    >
      <Icon className="size-4" />
      {label}
    </Link>
  );
}
